package com.isaias.fyattravel.web.layout

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.theme.ThemeColors

/**
 * Navigation item component for sidebar with SVG icon support
 */
@Composable
fun NavItem(
    icon: String,
    label: String,
    active: Boolean = false,
    isExpanded: Boolean = true,
    svgPath: String? = null,
    onClick: () -> Unit,
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(if (isExpanded) JustifyContent.FlexStart else JustifyContent.Center)
            gap(if (isExpanded) 0.75.em else 0.em)
            padding(if (isExpanded) 0.75.em else 0.6.em)
            borderRadius(0.75.em)
            if (active) {
                property("background", "linear-gradient(135deg, #AE7930, #8B5E24)")
                color(Color("#FFFFFF"))
                property("box-shadow", "0 3px 10px rgba(174,121,48,0.25)")
            } else {
                backgroundColor(Color.transparent)
                color(Color("#000000"))
            }
            cursor("pointer")
            fontWeight(if (active) "700" else "600")
            marginBottom(if (isExpanded) 0.2.em else 0.35.em)
            property("transition", "all 0.2s ease")
            whiteSpace("nowrap")
        }
        onClick { onClick() }
    }) {
        // Icon container - always visible
        Div({
            style {
                width(24.px)
                height(24.px)
                minWidth(24.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
        }) {
            if (svgPath != null) {
                Span({
                    style {
                        width(20.px)
                        height(20.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        color(if (active) Color("#FFFFFF") else Color("#000000"))
                    }
                    ref { element ->
                        element.innerHTML = svgPath
                        onDispose {}
                    }
                })
            } else {
                Span({
                    style {
                        fontSize(1.1.em)
                        textAlign("center")
                    }
                }) { Text(icon) }
            }
        }

        if (isExpanded) {
            Span({
                style {
                    fontSize(0.9.em)
                    property("transition", "opacity 0.2s ease")
                }
            }) { Text(label) }
        }
    }
}

// SVG icons as inline strings (20x20, stroke-based, modern)
object SvgIcons {
    const val HOME = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>"""

    const val CATEGORIES = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>"""

    const val FLIGHTS = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16l-4-4 1.9-4.8A1 1 0 0018 6h0a1 1 0 00-.8.4L13 12H8l-2 2h4l1 3h2l1-2h3.2l1.3 1.3a1 1 0 001.5-1.3z"/><line x1="3" y1="20" x2="21" y2="20"/></svg>"""

    const val HOTEL = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><rect x="9" y="13" width="6" height="8"/></svg>"""

    const val CAR = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 13l2-6h14l2 6"/><path d="M5 13h14v4a1 1 0 01-1 1h-1"/><path d="M7 18h10"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>"""

    const val VISA = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>"""

    const val EXPLORE = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>"""

    const val CART = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 001.95-1.57l1.65-8.43H6"/></svg>"""

    const val PROFILE = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>"""

    const val SIGN_IN = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>"""

    const val CONTACT = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.81 19.79 19.79 0 01.02 2.18 2 2 0 012 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14v2.92z"/></svg>"""

    const val ADMIN = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>"""

    const val SIGN_OUT = """<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>"""
}
